import { marked } from 'marked';
import yaml from 'js-yaml';
import Handlebars from 'handlebars/runtime';
import $ from 'jquery';
import utils from 'lib/utils';
import ef from 'ef/ef';
import LocaleService from 'services/loader-handler-services/locale-service';
import DOMPurify from 'dompurify';

// the inline markup the export dialect allows inside a paragraph
const EXPORT_INLINE_TAGS = ['strong', 'em', 'b', 'i', 'u', 's', 'sup', 'sub', 'br', 'a'];
const EXPORT_INLINE_ATTRS = ['href'];
// the block markup a rich-text value may carry on its own
const EXPORT_BLOCK_TAGS = EXPORT_INLINE_TAGS.concat(['p', 'ul', 'ol', 'li']);
const EXPORT_BLOCK_ROOTS = ['P', 'UL', 'OL'];

/**
 * Normalises a rich-text value to a sequence of paragraphs and lists: inline runs and text are wrapped in <p>,
 * existing <p>/<ul>/<ol> are kept, everything else is dropped.
 */
function exportParagraphs(html) {
  const source = document.createElement('div');
  source.innerHTML = DOMPurify.sanitize(html, { ALLOWED_TAGS: EXPORT_BLOCK_TAGS, ALLOWED_ATTR: EXPORT_INLINE_ATTRS });
  const output = document.createElement('div');
  let paragraph = null;
  Array.from(source.childNodes).forEach(node => {
    if (node.nodeType === Node.ELEMENT_NODE && EXPORT_BLOCK_ROOTS.includes(node.tagName)) {
      paragraph = null;
      output.appendChild(node);
    } else if (paragraph || node.nodeType !== Node.TEXT_NODE || node.textContent.trim()) {
      paragraph = paragraph ?? output.appendChild(document.createElement('p'));
      paragraph.appendChild(node);
    }
  });
  Array.from(output.children).forEach(child => {
    if (child.tagName === 'P' && !child.textContent.trim() && !child.querySelector('br')) {
      child.remove();
    }
  });
  return output.innerHTML;
}

const HandlebarsExtensions = {
  _blocks: [],
  helpers: {
    partial: function (name, ctx, options) {
      const ps = Handlebars.partials;
      if (typeof ps[name] !== 'function') {
        ps[name] = Handlebars.compile(ps[name]);
      }
      if (options.hash) {
        ctx = $.extend({}, ctx, options.hash);
      }
      return new Handlebars.SafeString(ps[name](ctx, options));
    },
    block: function (name) {
      const blocks = HandlebarsExtensions._blocks;
      let content = blocks && blocks[name];
      if (content) {
        content = new Handlebars.SafeString(content.join('\n'));
      } else {
        content = null;
      }
      return content;
    },
    content_for: function (name, options) {
      const blocks = HandlebarsExtensions._blocks;
      const block = blocks[name] || (blocks[name] = []);

      block.push(options.fn(this));
    },
    /**
     ** Warning! **
     * This exposes an XSS vulnerability that was fixed in this PR
     *   https://github.com/EverFi/ContentPartnerAPI/pull/140
     * SafeString will not HTML encode the result and as a consequence will execute script in the translation files
     *
     * Several of the views inject HTML tags to style things or add other content, so this was reverted
     *
     * To mitigate this one of two things can be done:
     *    * Don't call SafeString and just return the result of LocalService.t(key). Handlebars will HTML encode the result.
     *    * Handle the HTML encoding yourself and use SafeString since the result is already encoded
     *
     **/

    t: function (key, options) {
      const result = LocaleService.t(key, options.hash);
      if (ef.forgeMode) {
        const gotResult = result === key ? 'no' : 'yes';
        return new Handlebars.SafeString(
          `<span data-forgeaction='editlocale' data-key='${key}' data-translated='${gotResult}'>${result}</span>`
        );
      }
      return new Handlebars.SafeString(result);
    },
    pt: function (key) {
      // translates 'plain text only' fields (avoids forge-mode)
      const result = LocaleService.t(key);
      return new Handlebars.SafeString(result);
    },
    xt: function (key, options) {
      // translates for export: never a forge-mode span, and only the inline markup the export dialect allows
      const result = LocaleService.t(key, options?.hash);
      const sanitized = DOMPurify.sanitize(result, {
        ALLOWED_TAGS: EXPORT_INLINE_TAGS,
        ALLOWED_ATTR: EXPORT_INLINE_ATTRS,
      });
      return new Handlebars.SafeString(sanitized);
    },
    xp: function (key, options) {
      // translates a body field for export as one or more paragraphs; see exportParagraphs
      const result = LocaleService.t(key, options?.hash);
      return new Handlebars.SafeString(exportParagraphs(result));
    },
    any: function () {
      // true when any argument (other than the trailing Handlebars options) is truthy; empty arrays count as falsy
      return Array.from(arguments)
        .slice(0, -1)
        .some(argument => (Array.isArray(argument) ? argument.length > 0 : Boolean(argument)));
    },
    startsWith: function (value, prefix) {
      return typeof value === 'string' && value.startsWith(prefix);
    },
    indexToLetter: function (index) {
      // 0 -> A, 25 -> Z, 26 -> AA
      let letters = '';
      let remaining = parseInt(index, 10);
      do {
        letters = String.fromCharCode(65 + (remaining % 26)) + letters;
        remaining = Math.floor(remaining / 26) - 1;
      } while (remaining >= 0);
      return letters;
    },
    isCorrectAnswer: function (question, answer) {
      // correct_answers holds ids as strings; the deprecated correct_answer holds one id as a number
      if (!question || !answer) {
        return false;
      }
      if (Array.isArray(question.correct_answers)) {
        return question.correct_answers.some(id => String(id) === String(answer.id));
      }
      return question.correct_answer !== undefined && String(question.correct_answer) === String(answer.id);
    },
    customResponse: function (customResponses, questionIndex, answerIndex) {
      // custom_responses is index-aligned with answers; some courses nest one array per question
      if (!Array.isArray(customResponses)) {
        return undefined;
      }
      const perQuestion = Array.isArray(customResponses[questionIndex]) ? customResponses[questionIndex] : customResponses;
      const response = perQuestion[answerIndex];
      return typeof response === 'string' ? response : undefined;
    },
    f: function (action, key, cid, forge_path, label) {
      // returns a forge-mode 'action' tag for a field 'key' that is tied to its parent 'block' CID code and objpath
      if (ef.forgeMode) {
        const result = LocaleService.t(key);
        const gotResult = result === key ? 'no' : 'yes';
        return new Handlebars.SafeString(
          `<span data-forgeaction='${action}' data-key='${key}' data-sdkcid='${cid}' data-objpath='${forge_path}' data-label='${label}' data-translated='${gotResult}'></span>`
        );
      }
      return null;
    },
    m: function (content) {
      return new Handlebars.SafeString(marked.parse(content));
    },
    tm: function (key) {
      const result = marked.parse(LocaleService.t(key));
      return new Handlebars.SafeString(result);
    },
    indexToCount: function (i) {
      const sum = i + 1;
      return LocaleService.t(sum.toString());
    },
    indexFromOne: function (index) {
      return parseInt(index) + 1;
    },
    parseInt: function (i) {
      return parseInt(i);
    },
    assetPath: function (path) {
      const fullPath = utils.coursePath(path);
      return new Handlebars.SafeString(fullPath);
    },
    times: function (n, block) {
      if (n === undefined || isNaN(n) || n === null || n === '') {
        return;
      }
      return [...new Array(n).keys()].reduce((result, index) => {
        let data;
        if (block.data) {
          data = Handlebars.createFrame(block.data);
        }
        if (data) {
          data.index = index;
        }
        return (result += block.fn(index, { data: data }));
      }, '');
    },
    style: function (value) {
      return Object.keys(value)
        .map(e => `${e}: ${value[e]}`)
        .join(';');
    },
    yamlize: function (value, indent) {
      const yamlized = yaml.dump(value, { indent });
      return indent === undefined ? yamlized : yamlized.replace(/\n/g, `\n${' '.repeat(indent)}`);
    },
    jsonize: function (value) {
      return JSON.stringify(value);
    },
    equal: function (first, second) {
      return first === second;
    },
    not_equal: function (first, second) {
      return first !== second;
    },
    subIf: function (v1, success, fail, operator, v2) {
      const conditions = {
        ...HandlebarsExtensions.helpers.getExpOperators(),
        undefined: () => false,
      };
      const isOperatorString = typeof operator === 'string' || operator instanceof String;
      return isOperatorString ? (conditions[operator](v1, v2) ? success : fail) : v1 ? success : fail;
    },
    compare: function (v1, operator, v2, options) {
      const conditions = {
        ...HandlebarsExtensions.helpers.getExpOperators(),
        undefined: () => options.inverse(this),
      };
      return conditions[operator](v1, v2) ? options.fn(this) : options.inverse(this);
    },
    getExpOperators: function () {
      return {
        '===': (v1, v2) => v1 === v2,
        '!==': (v1, v2) => v1 !== v2,
        '<': (v1, v2) => v1 < v2,
        '<=': (v1, v2) => v1 <= v2,
        '>': (v1, v2) => v1 > v2,
        '>=': (v1, v2) => v1 >= v2,
        '&&': (v1, v2) => v1 && v2,
        '||': (v1, v2) => v1 || v2,
      };
    },
    defined: function () {
      return !Array.from(arguments).some(argument => {
        return argument === undefined;
      });
    },
    /**
     * Concats N items together
     * Usage: <div>{{ (concat 'one ' 'two ' 'three ', 'N') }}</div> => <div>one two three N</div>
     */
    concat: function (...args) {
      // slice to throw away last object since it contains handlebars configuration
      return args.slice(0, -1).reduce(function (result, arg) {
        return (result += arg);
      }, '');
    },
    math: function (lvalue, operator, rvalue) {
      return utils.math(lvalue, operator, rvalue);
    },
    obj_values_to_string: function (object) {
      if (!object) {
        return '';
      }
      return Object.values(object).join(' ');
    },
    faTransform: function (operation, value) {
      if (!value) {
        return;
      }
      return {
        rotate: value => `rotate-${value}`,
        horizontal: value => (value < 0 ? `left-${Math.abs(value)}` : `right-${value}`),
        vertical: value => (value < 0 ? `up-${Math.abs(value)}` : `down-${value}`),
        scale: value => (value < 0 ? `shrink-${Math.abs(value)}` : `grow-${value}`),
        flip: value => {
          if (value.split(' ').length === 2) {
            return 'flip-v flip-h';
          } else {
            return `flip-${value}`;
          }
        },
      }[operation]?.(value);
    },
    ifIsString: function (item, options) {
      if (typeof item === 'string' || item instanceof String) {
        return options.fn(this);
      } else {
        return options.inverse(this);
      }
    },
    isArrayLengthGreaterThan: function (array, threshold, options) {
      if (array && Array.isArray(array) && array.length > threshold) {
        return options.fn(this);
      } else {
        return options.inverse(this);
      }
    }
  },
};

for (const helper in HandlebarsExtensions.helpers) {
  if (
    Object.prototype.hasOwnProperty.call(HandlebarsExtensions.helpers, helper) &&
    typeof HandlebarsExtensions.helpers[helper] === 'function'
  ) {
    Handlebars.registerHelper(helper, HandlebarsExtensions.helpers[helper]);
  }
}

export default HandlebarsExtensions;
